import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Sparkles,
  CheckCircle,
  Clock,
  Send,
  FileText,
  Smartphone,
  Mail,
  Megaphone,
  CircleHelp,
  Star,
  Images,
  Wrench,
  File,
} from 'lucide-react';
import { NotionDatePicker } from './NotionDatePicker';
import { contentPlanService, type ContentItem } from '../../services/contentPlanService';
import { usePlanFeatures } from '../../hooks/usePlanFeatures';
import PlanUpsell from '../ui/PlanUpsell';
import { MARKS } from '../../lib/marks';
import api from '../../services/api';
import Drawer from '../ui/Drawer';
import {
  Empty,
  Eyebrow,
  Field,
  HairlineGrid,
  PageHeader,
  Pill,
  Stat,
  Tag,
  TextLink,
  type TagTone,
} from '../ui/kit';

interface Website {
  id: string;
  domain: string;
  businessName?: string;
}

/**
 * Where an item that needs the owner to do something actually sends them.
 * The drawer used to state the requirement ("upload photos before this can be
 * published") and then leave them on a dead end.
 */
function actionDestination(
  type: string,
  websiteId: string
): { to: string; label: string } | null {
  if (!websiteId) return null;
  switch (type) {
    case 'gallery':
      return { to: `/dashboard/websites/${websiteId}/edit`, label: 'Add your photos' };
    case 'testimonial':
      return { to: '/dashboard/reviews', label: 'Go to reviews' };
    default:
      return { to: `/dashboard/websites/${websiteId}/edit`, label: 'Open the website editor' };
  }
}

const TYPE_LABELS: Record<string, string> = {
  blog: 'Blog post',
  social: 'Social post',
  email: 'Email',
  promo: 'Promotion',
  faq: 'FAQ',
  testimonial: 'Testimonial',
  gallery: 'Photos',
  'website-update': 'Website update',
};

/**
 * The type chip used to be a `NotionSelect` with `onChange={() => {}}` — it
 * opened a menu, let you pick, and threw the choice away. It's a label, so it's
 * a label: the kit's Tag, on the kit's tones rather than eight raw hex values.
 */
const TYPE_TONE: Record<string, TagTone> = {
  blog: 'info',
  social: 'violet',
  email: 'live',
  promo: 'warn',
  faq: 'neutral',
  testimonial: 'live',
  gallery: 'violet',
  'website-update': 'ink',
};

function TypeTag({ type }: { type: string }) {
  return <Tag tone={TYPE_TONE[type] ?? 'neutral'}>{TYPE_LABELS[type] ?? type}</Tag>;
}

export default function ContentPlanPage() {
  const queryClient = useQueryClient();
  const { features } = usePlanFeatures();
  const autoContent = features.autoContent;
  const [selectedItem, setSelectedItem] = useState<ContentItem | null>(null);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedContent, setEditedContent] = useState('');
  const [editedDate, setEditedDate] = useState('');
  const contentTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Fetch websites
  const { data: websitesData } = useQuery({
    queryKey: ['websites'],
    queryFn: async () => {
      const res = await api.get('/api/websites');
      return (res.data.data?.websites || res.data.data) as Website[];
    },
  });
  const websites = websitesData || [];
  const [selectedWebsiteId, setSelectedWebsiteId] = useState<string>('');
  const websiteId = selectedWebsiteId || websites[0]?.id || '';

  // Fetch content items
  const { data: itemsData } = useQuery({
    queryKey: ['content-plan', websiteId],
    queryFn: () => contentPlanService.getItems({ websiteId }),
    enabled: !!websiteId,
  });

  const contentItems = itemsData?.items || [];

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<ContentItem>) =>
      contentPlanService.updateItem(id, data),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['content-plan'] }),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => contentPlanService.deleteItem(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['content-plan'] });
      queryClient.setQueryData(['content-plan', websiteId], (old: { items: ContentItem[]; total: number } | undefined) => {
        if (!old) return old;
        return { ...old, items: old.items.filter(i => i.id !== id), total: old.total - 1 };
      });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['content-plan'] }),
  });

  // Sync edited fields when selectedItem changes
  useEffect(() => {
    if (selectedItem) {
      setEditedTitle(selectedItem.title);
      setEditedContent(selectedItem.content);
      setEditedDate(selectedItem.scheduledDate ? selectedItem.scheduledDate.split('T')[0] : '');
    }
  }, [selectedItem]);

  // Auto-resize textareas to fit content
  useEffect(() => {
    const textarea = contentTextareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }, [editedContent]);


  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'blog':
        return <FileText className="w-5 h-5" />;
      case 'social':
        return <Smartphone className="w-5 h-5" />;
      case 'email':
        return <Mail className="w-5 h-5" />;
      case 'promo':
        return <Megaphone className="w-5 h-5" />;
      case 'faq':
        return <CircleHelp className="w-5 h-5" />;
      case 'testimonial':
        return <Star className="w-5 h-5" />;
      case 'gallery':
        return <Images className="w-5 h-5" />;
      case 'website-update':
        return <Wrench className="w-5 h-5" />;
      default:
        return <File className="w-5 h-5" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'posted':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'scheduled':
        return <Clock className="w-4 h-4 text-blue-600" />;
      case 'draft':
        return <Send className="w-4 h-4 text-zinc-400" />;
      default:
        return null;
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return 'Not scheduled';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getRelativeTime = (date: string | null) => {
    if (!date) return 'No date';
    const now = new Date();
    const diffTime = new Date(date).getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'Posted';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays < 7) return `In ${diffDays} days`;
    if (diffDays < 14) return 'Next week';
    if (diffDays < 30) return `In ${Math.floor(diffDays / 7)} weeks`;
    const months = Math.floor(diffDays / 30);
    return `In ${months} ${months === 1 ? 'month' : 'months'}`;
  };

  const handleApprove = (id: string) => {
    updateMutation.mutate({ id, status: 'scheduled' });
  };

  const handleDecline = (id: string) => {
    deleteMutation.mutate(id);
  };

  const currentWebsite = websites.find((w) => w.id === websiteId);

  /**
   * The decision the item is actually waiting on.
   *
   * This used to show Approve/Decline only when `status === 'scheduled'` — but
   * scheduled *is* the approved state, so Approve re-wrote the status it
   * already had, and a `draft` (the one state genuinely waiting on an answer)
   * offered nothing at all. Each state now gets the action that belongs to it.
   */
  const renderDrawerFooter = (item: ContentItem) => {
    if (item.status === 'draft') {
      return (
        <div className="space-y-3">
          <p className="text-[13px] text-muted">
            Nothing goes on your site until you approve it.
          </p>
          <div className="flex gap-2">
            <Pill
              className="flex-1"
              onClick={() => {
                handleApprove(item.id);
                setSelectedItem(null);
              }}
            >
              Approve &amp; schedule
            </Pill>
            <Pill
              tone="danger"
              className="flex-1"
              onClick={() => {
                handleDecline(item.id);
                setSelectedItem(null);
              }}
            >
              Decline
            </Pill>
          </div>
        </div>
      );
    }

    if (item.status === 'scheduled') {
      return (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="flex items-center gap-1.5 text-[13px] text-muted">
            <Clock className="h-4 w-4" />
            {autoContent ? 'Posts automatically on' : 'Planned for'}{' '}
            {formatDate(item.scheduledDate)}
          </p>
          <Pill
            tone="danger"
            size="sm"
            onClick={() => {
              handleDecline(item.id);
              setSelectedItem(null);
            }}
          >
            Don&apos;t post this
          </Pill>
        </div>
      );
    }

    return (
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-1.5 text-[13px] text-emerald-700">
          <CheckCircle className="h-4 w-4" />
          Posted on {formatDate(item.scheduledDate)}
        </p>
        {currentWebsite?.domain && (
          <TextLink href={`https://${currentWebsite.domain}`}>View your website →</TextLink>
        )}
      </div>
    );
  };

  // Debounced save
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const handleFieldChange = (field: 'title' | 'content' | 'date', value: string) => {
    if (!selectedItem) return;

    const updatedItem = { ...selectedItem };

    if (field === 'title') {
      setEditedTitle(value);
      updatedItem.title = value;
    } else if (field === 'content') {
      setEditedContent(value);
      updatedItem.content = value;
    } else if (field === 'date') {
      setEditedDate(value);
      updatedItem.scheduledDate = new Date(value).toISOString();
    }

    setSelectedItem(updatedItem);

    // Debounced save to API
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      updateMutation.mutate({
        id: updatedItem.id,
        title: updatedItem.title,
        content: updatedItem.content,
        scheduledDate: updatedItem.scheduledDate,
      });
    }, 500);
  };

  const contentStats = {
    posted: contentItems.filter(item => item.status === 'posted').length,
    scheduled: contentItems.filter(item => item.status === 'scheduled').length,
    draft: contentItems.filter(item => item.status === 'draft').length,
    total: contentItems.length,
    nextPost: contentItems.find(item => item.status === 'scheduled' && item.scheduledDate && new Date(item.scheduledDate) > new Date())
  };

  // Group content by date for timeline view
  const groupedContent = contentItems.reduce((acc, item) => {
    const dateKey = item.scheduledDate
      ? new Date(item.scheduledDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      : 'No date';
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(item);
    return acc;
  }, {} as Record<string, ContentItem[]>);

  // Sort dates chronologically
  const sortedDates = Object.keys(groupedContent).sort((a, b) => {
    const dateA = new Date(a);
    const dateB = new Date(b);
    return dateA.getTime() - dateB.getTime();
  });

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Content"
        title={autoContent ? 'Auto-Posting Schedule' : 'Your Website Roadmap'}
        description={autoContent
          ? "Your website automatically posts optimized content. Review upcoming posts and decline any you don't want."
          : 'A timeline of the planned setup and maintenance steps for your website. Review what’s coming and take action where needed.'}
        actions={
          <>
          {websites.length > 1 && (
            <select
              value={websiteId}
              onChange={(e) => setSelectedWebsiteId(e.target.value)}
              className="input-field w-auto min-w-48"
            >
              {websites.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.businessName || w.domain}
                </option>
              ))}
            </select>
          )}
          <Tag tone="live" dot>
            {autoContent ? 'Auto-posting active' : 'Roadmap on track'}
          </Tag>
          </>
        }
      />

      {/* Starter sees a roadmap it has to work through itself — the thing worth
          selling here is having it written and published for them. */}
      {!autoContent && (
        <PlanUpsell
          mark={MARKS.autoContent}
          requiredPlan="professional"
          title="Have this written and posted for you"
          description="On Starter this page is a roadmap you work through. From Managed up, the content gets written and published on schedule without you."
          includes={[
            'Monthly content updates, written for you',
            'Published on schedule, automatically',
            'Advanced and local SEO on every page',
          ]}
        />
      )}

      {/* Stats Overview */}
      <HairlineGrid cols="grid-cols-1 md:grid-cols-3">
        <Stat label="Scheduled Posts" value={contentStats.scheduled} />
        <Stat label="Already Posted" value={contentStats.posted} />
        <Stat
          label={autoContent ? 'Next Auto-Post' : 'Next Step'}
          value={<span className="block truncate text-base">{contentStats.nextPost?.title || 'None scheduled'}</span>}
          note={contentStats.nextPost ? formatDate(contentStats.nextPost?.scheduledDate || null) : undefined}
        />
      </HairlineGrid>

      {/* Timeline View */}
      <div className="space-y-8">
        {/* On Starter the upsell above already carries the drawing and the
            explanation, so an empty timeline stays silent under it. */}
        {sortedDates.length === 0 && autoContent && (
          <Empty
            framed
            mark={MARKS.content}
            eyebrow="Schedule"
            title="Nothing scheduled yet"
            description="Posts appear here as soon as the first month of content is planned for your site."
            hint="Each item can be edited or declined before it goes out."
          />
        )}
        {sortedDates.map((dateKey, dateIndex) => (
          <div key={dateKey} className="relative">
            {/* Date Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary"></div>
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">{dateKey}</div>
                  <div className="text-[13px] text-muted">
                    {getRelativeTime(groupedContent[dateKey][0]?.scheduledDate)}
                  </div>
                </div>
              </div>
              <div className="flex-1 h-px bg-zinc-200"></div>
            </div>

            {/* Content Items for This Date */}
            <div className="grid grid-cols-1 gap-3 ml-4">
              {groupedContent[dateKey].map((item, itemIndex) => {
                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: (dateIndex * 0.1) + (itemIndex * 0.05) }}
                  >
                    {/* Content Card */}
                    <div
                      onClick={() => setSelectedItem(item)}
                      className={`cursor-pointer rounded-xl border border-zinc-200 p-4 transition-colors ${
                        item.status === 'posted'
                          ? 'bg-white/40 opacity-60 hover:opacity-100'
                          : item.action === 'user-action-required'
                          ? 'bg-white hover:border-zinc-300'
                          : 'bg-white hover:border-zinc-300'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Icon — tinted by content type */}
                        <span className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-zinc-100 text-zinc-600">
                          {getTypeIcon(item.type)}
                        </span>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="flex-1 min-w-0">
                              <h3 className="mb-1 text-base font-semibold text-primary">{item.title}</h3>
                              <p className="line-clamp-1 text-[15px] text-muted">{item.content}</p>
                            </div>
                            <div className="flex-shrink-0">
                              <TypeTag type={item.type} />
                            </div>
                          </div>

                          {/* Meta info */}
                          <div className="flex items-center justify-between mt-3">
                            <div className="flex items-center gap-3 text-[13px] text-muted">
                              {item.action === 'user-action-required' && (
                                <Tag tone="warn">Action Required</Tag>
                              )}
                              {item.platform && item.platform.length > 0 && (
                                <span>{item.platform.join(', ')}</span>
                              )}
                              {item.keywords && item.keywords.length > 0 && (
                                <span>{item.keywords.length} {item.keywords.length === 1 ? 'keyword' : 'keywords'}</span>
                              )}
                            </div>

                            {/* Status indicator */}
                            <div className="flex items-center gap-2">
                              {item.status === 'posted' && (
                                <span className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-eyebrow text-emerald-700">
                                  <CheckCircle className="w-3.5 h-3.5" />
                                  Posted
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Content Detail Drawer */}
      <Drawer
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        eyebrow={selectedItem ? TYPE_LABELS[selectedItem.type] ?? 'Update' : undefined}
        title={editedTitle || 'Untitled'}
        subtitle={
          selectedItem?.status === 'posted'
            ? `Published ${formatDate(selectedItem.scheduledDate)}`
            : selectedItem?.scheduledDate
              ? `${autoContent ? 'Posts' : 'Planned for'} ${formatDate(selectedItem.scheduledDate)}`
              : 'Not scheduled'
        }
        width="xl"
        footer={selectedItem ? renderDrawerFooter(selectedItem) : null}
      >
        {selectedItem && (
          <div className="space-y-8">
            {/* Something only they can do — with the way to go do it. */}
            {selectedItem.action === 'user-action-required' && selectedItem.status !== 'posted' && (
              <section className="rounded-xl border border-amber-600/20 bg-amber-50 p-4">
                <Eyebrow className="text-amber-800">Needs you</Eyebrow>
                <p className="mt-2 text-[14px] leading-relaxed text-amber-900">
                  {selectedItem.type === 'gallery' &&
                    'Photos of your own work go here. Until you upload some, the page keeps showing stock images.'}
                  {selectedItem.type === 'testimonial' &&
                    'Read this through before it goes on your site — once approved it appears publicly under your business name.'}
                  {selectedItem.type !== 'gallery' &&
                    selectedItem.type !== 'testimonial' &&
                    'This step needs a decision from you before it can go ahead.'}
                </p>
                {(() => {
                  const destination = actionDestination(selectedItem.type, websiteId);
                  return destination ? (
                    <div className="mt-4">
                      <Pill to={destination.to} size="sm" arrow onClick={() => setSelectedItem(null)}>
                        {destination.label}
                      </Pill>
                    </div>
                  ) : null;
                })()}
              </section>
            )}

            {/*
              The words that end up on their website, under their business
              name. Read first, edit second — a posted item is a record and
              shows as prose; anything still to come is editable in a field
              that looks like one, rather than in the borderless textarea this
              used to be, which read as a caption you weren't meant to touch.
            */}
            <section>
              <div className="mb-3 flex items-center justify-between gap-3">
                <Eyebrow>
                  {selectedItem.status === 'posted' ? 'What was published' : 'What will be published'}
                </Eyebrow>
                {selectedItem.status !== 'posted' && (
                  <span className="font-mono text-[11px] text-zinc-400">Saves as you type</span>
                )}
              </div>

              {selectedItem.status === 'posted' ? (
                <div className="rounded-xl border border-zinc-200 bg-paper-tint p-5">
                  <h3 className="font-display text-lg font-bold tracking-tight text-primary">
                    {selectedItem.title}
                  </h3>
                  <p className="mt-3 whitespace-pre-wrap text-[15px] leading-relaxed text-primary">
                    {selectedItem.content || 'No text was recorded for this item.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-5">
                  <Field label="Title" htmlFor="content-title">
                    <input
                      id="content-title"
                      value={editedTitle}
                      onChange={(e) => handleFieldChange('title', e.target.value)}
                      maxLength={512}
                      placeholder="Post title"
                      className="input-field"
                    />
                  </Field>
                  <Field
                    label="Text"
                    htmlFor="content-body"
                    hint="This is exactly what goes on your site. Change anything you don't like."
                  >
                    <textarea
                      id="content-body"
                      ref={contentTextareaRef}
                      value={editedContent}
                      onChange={(e) => handleFieldChange('content', e.target.value)}
                      placeholder="Content text"
                      className="input-field resize-none overflow-hidden leading-relaxed"
                      style={{ minHeight: '8rem' }}
                    />
                  </Field>
                </div>
              )}
            </section>

            {/* The mechanics, under the thing that matters. */}
            <section>
              <Eyebrow className="mb-3">Details</Eyebrow>
              <dl className="divide-y divide-zinc-200 rounded-xl border border-zinc-200">
                <div className="flex items-center gap-4 px-4 py-3">
                  <dt className="w-28 shrink-0 font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
                    Type
                  </dt>
                  <dd className="min-w-0 flex-1">
                    <TypeTag type={selectedItem.type} />
                  </dd>
                </div>
                <div className="flex items-center gap-4 px-4 py-3">
                  <dt className="w-28 shrink-0 font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
                    Scheduled
                  </dt>
                  <dd className="min-w-0 flex-1">
                    {selectedItem.status === 'posted' ? (
                      <span className="text-[14px] text-primary">
                        {formatDate(selectedItem.scheduledDate)}
                      </span>
                    ) : (
                      <NotionDatePicker
                        value={editedDate}
                        onChange={(value) => handleFieldChange('date', value)}
                        variant="inline"
                      />
                    )}
                  </dd>
                </div>
                <div className="flex items-center gap-4 px-4 py-3">
                  <dt className="w-28 shrink-0 font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
                    Status
                  </dt>
                  <dd className="flex min-w-0 flex-1 items-center gap-2">
                    {getStatusIcon(selectedItem.status)}
                    <span className="text-[14px] capitalize text-primary">{selectedItem.status}</span>
                  </dd>
                </div>
                {selectedItem.platform && selectedItem.platform.length > 0 && (
                  <div className="flex items-center gap-4 px-4 py-3">
                    <dt className="w-28 shrink-0 font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
                      Platforms
                    </dt>
                    <dd className="flex min-w-0 flex-1 flex-wrap gap-2">
                      {selectedItem.platform.map((platform) => (
                        <Tag key={platform}>{platform}</Tag>
                      ))}
                    </dd>
                  </div>
                )}
                {selectedItem.keywords && selectedItem.keywords.length > 0 && (
                  <div className="flex items-center gap-4 px-4 py-3">
                    <dt className="w-28 shrink-0 font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
                      Keywords
                    </dt>
                    <dd className="flex min-w-0 flex-1 flex-wrap gap-2">
                      {selectedItem.keywords.map((keyword) => (
                        <Tag key={keyword}>{keyword}</Tag>
                      ))}
                    </dd>
                  </div>
                )}
              </dl>
            </section>
          </div>
        )}
      </Drawer>
    </div>
  );
}
